import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  Plus, Search, RefreshCw, MoreHorizontal, Copy, Trash2, Edit, Eye,
  FileText, Code, BarChart3, ArrowRight, ExternalLink, CheckCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  draft: 'bg-slate-500',
  active: 'bg-green-500',
  archived: 'bg-gray-400'
};

const FormList = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showEmbedDialog, setShowEmbedDialog] = useState(false);
  const [formToDelete, setFormToDelete] = useState(null);
  const [embedCodes, setEmbedCodes] = useState(null);

  const fetchForms = useCallback(async () => {
    try {
      let url = `${API_URL}/api/marketing/forms?token=${token}&limit=50`;
      if (statusFilter && statusFilter !== 'all') url += `&status=${statusFilter}`;
      if (searchQuery) url += `&search=${encodeURIComponent(searchQuery)}`;

      const res = await fetch(url);
      const data = await res.json();

      if (data.success) {
        setForms(data.forms || []);
      }
    } catch (error) {
      console.error('Error fetching forms:', error);
    } finally {
      setLoading(false);
    }
  }, [token, statusFilter, searchQuery]);

  useEffect(() => {
    fetchForms();
  }, [fetchForms]);

  const handlePublish = async (formId) => {
    try {
      const res = await fetch(`${API_URL}/api/marketing/forms/${formId}/publish?token=${token}`, {
        method: 'POST'
      });
      const data = await res.json();
      
      if (data.success) {
        toast.success('Form published');
        fetchForms();
      } else {
        toast.error(data.detail || 'Failed to publish');
      }
    } catch (error) {
      toast.error('Error publishing form');
    }
  };

  const handleDuplicate = async (formId) => {
    try {
      const res = await fetch(`${API_URL}/api/marketing/forms/${formId}/duplicate?token=${token}`, {
        method: 'POST'
      });
      const data = await res.json();
      
      if (data.success) {
        toast.success('Form duplicated');
        fetchForms();
      } else {
        toast.error(data.detail || 'Failed to duplicate');
      }
    } catch (error) {
      toast.error('Error duplicating form');
    }
  };

  const handleDelete = async () => {
    if (!formToDelete) return;

    try {
      const res = await fetch(`${API_URL}/api/marketing/forms/${formToDelete}?token=${token}`, {
        method: 'DELETE'
      });
      const data = await res.json();
      
      if (data.success) {
        toast.success('Form deleted');
        setShowDeleteDialog(false);
        setFormToDelete(null);
        fetchForms();
      } else {
        toast.error(data.detail || 'Failed to delete');
      }
    } catch (error) {
      toast.error('Error deleting form');
    }
  };

  const handleGetEmbedCode = async (formId) => {
    try {
      const res = await fetch(`${API_URL}/api/marketing/forms/${formId}/embed-code?token=${token}`);
      const data = await res.json();
      
      if (data.success) {
        setEmbedCodes(data);
        setShowEmbedDialog(true);
      }
    } catch (error) {
      toast.error('Error getting embed code');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <RefreshCw className="w-8 h-8 animate-spin text-accent" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="form-list">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Forms</h1>
          <p className="text-slate-500 dark:text-slate-400">Create and manage lead capture forms</p>
        </div>
        <Button onClick={() => navigate('/marketing/forms/new')}>
          <Plus className="w-4 h-4 mr-2" />
          Create Form
        </Button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search forms..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={statusFilter || 'all'} onValueChange={(v) => setStatusFilter(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-32">
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="archived">Archived</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={fetchForms}>
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      {/* Form Grid */}
      {forms.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <FileText className="w-16 h-16 mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Forms Yet</h3>
            <p className="text-slate-500 mb-4">Create your first form to start capturing leads</p>
            <Button onClick={() => navigate('/marketing/forms/new')}>
              Create Form
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {forms.map((form) => (
            <Card key={form.id} className="hover:shadow-md transition-shadow">
              <CardContent className="pt-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold truncate">{form.name}</h3>
                      <Badge variant="outline" className="capitalize">
                        <span className={`w-2 h-2 rounded-full mr-1.5 ${STATUS_COLORS[form.status]}`}></span>
                        {form.status}
                      </Badge>
                    </div>
                    {form.description && (
                      <p className="text-sm text-slate-500 line-clamp-2 mt-1">{form.description}</p>
                    )}
                  </div>
                  
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm">
                        <MoreHorizontal className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => navigate(`/marketing/forms/${form.id}`)}>
                        <Edit className="w-4 h-4 mr-2" />
                        Edit
                      </DropdownMenuItem>
                      {form.status === 'active' && (
                        <>
                          <DropdownMenuItem onClick={() => window.open(`${API_URL}/api/marketing/forms/${form.id}/embed`, '_blank')}>
                            <Eye className="w-4 h-4 mr-2" />
                            Preview
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleGetEmbedCode(form.id)}>
                            <Code className="w-4 h-4 mr-2" />
                            Embed Code
                          </DropdownMenuItem>
                        </>
                      )}
                      {form.status === 'draft' && (
                        <DropdownMenuItem onClick={() => handlePublish(form.id)}>
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Publish
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem onClick={() => handleDuplicate(form.id)}>
                        <Copy className="w-4 h-4 mr-2" />
                        Duplicate
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={() => {
                          setFormToDelete(form.id);
                          setShowDeleteDialog(true);
                        }}
                        className="text-red-600"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 gap-2 mb-4">
                  <div className="text-center p-2 bg-slate-50 dark:bg-slate-800 rounded">
                    <p className="text-lg font-semibold">{form.fields?.length || 0}</p>
                    <p className="text-xs text-slate-500">Fields</p>
                  </div>
                  <div className="text-center p-2 bg-slate-50 dark:bg-slate-800 rounded">
                    <p className="text-lg font-semibold">{form.submission_count || 0}</p>
                    <p className="text-xs text-slate-500">Submissions</p>
                  </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between pt-4 border-t">
                  <span className="text-xs text-slate-500">
                    Created {form.created_at && format(new Date(form.created_at), 'MMM d, yyyy')}
                  </span>
                </div>

                {/* Open Button */}
                <Button 
                  variant="outline" 
                  className="w-full mt-3"
                  onClick={() => navigate(`/marketing/forms/${form.id}`)}
                >
                  Open Builder
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Form</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this form? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Embed Code Dialog */}
      <Dialog open={showEmbedDialog} onOpenChange={setShowEmbedDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Embed Form</DialogTitle>
            <DialogDescription>
              Copy the code below to embed this form on your website
            </DialogDescription>
          </DialogHeader>

          {embedCodes && (
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-1 block">Direct Link</label>
                <div className="flex gap-2">
                  <Input value={embedCodes.embed_url} readOnly className="text-sm" />
                  <Button variant="outline" size="sm" onClick={() => copyToClipboard(embedCodes.embed_url)}>
                    <Copy className="w-4 h-4" />
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => window.open(embedCodes.embed_url, '_blank')}>
                    <ExternalLink className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium mb-1 block">iFrame Embed</label>
                <div className="flex gap-2">
                  <Input value={embedCodes.iframe_code} readOnly className="text-sm font-mono" />
                  <Button variant="outline" size="sm" onClick={() => copyToClipboard(embedCodes.iframe_code)}>
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium mb-1 block">JavaScript Embed</label>
                <pre className="bg-slate-100 dark:bg-slate-800 p-3 rounded text-xs overflow-x-auto">
                  {embedCodes.script_code}
                </pre>
                <Button variant="outline" size="sm" className="mt-2" onClick={() => copyToClipboard(embedCodes.script_code)}>
                  <Copy className="w-4 h-4 mr-2" />
                  Copy Code
                </Button>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button onClick={() => setShowEmbedDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FormList;
